import { Button } from "components/ui/v2";
import { useMutation } from "@apollo/client";
import { WIDTH_MD } from "@constants/global";
import { useWindowWidth } from "@react-hook/window-size";
import { LiquidPillDropdown, type LiquidPillDropdownItem } from "components/ui/v2/liquid-pill-dropdown";
import { gqlUser } from "gql";
import {
  IDeleteUserCertificationInput,
  IDeleteUserCertificationRes
} from "gql/User/mutations";
import { AlertTriangle, Award, Building2, CalendarPlus, CalendarX, CheckCircle, Chrome, Clock, Cloud, CloudDownload, Code, Columns3, Database, Download, ExternalLink, FileBadge, GitBranch, Github, GraduationCap, Leaf, type LucideIcon, MoreVertical, Network, Pencil, Plus, Rocket, Star, Trash2, Users } from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useState } from "react";
import toast from "react-hot-toast";
import { Card } from "components/ui/v2/card";
import { GlassBadge } from "components/ui/v2/glass-badge";
import {
  ECertificationType,
  ETypeCertification,
  IUserCertification
} from "types/User.types";
import { getQueryOperator } from "utils/helpers";
import CertificationForm from "components/ui/v2/shared/bundle/profile/CertificationForm";

export const commonTypes = [
  ECertificationType.AZURE,
  ECertificationType.MICROSOFT,
  ECertificationType.GCP,
  ECertificationType.GOOGLE,
  ECertificationType.AWS,
  ECertificationType.GITHUB,
  ECertificationType.ORACLE,
  ECertificationType.MONGODB
];

export const getIconByType = (type: ECertificationType, size?: number) => {
  const sizeIcon = size || 30;
  const iconStyle = { fontSize: sizeIcon };

  switch (type) {
    case ECertificationType.AZURE:
      return (
        <Building2 className="h-4 w-4" />
      );
    case ECertificationType.MICROSOFT:
      return (
        <Building2 className="h-4 w-4" />
      );
    case ECertificationType.GCP:
      return (
        <Chrome className="h-4 w-4" />
      );
    case ECertificationType.GOOGLE:
      return (
        <Chrome className="h-4 w-4" />
      );
    case ECertificationType.AWS:
      return (
        <Cloud className="h-4 w-4" />
      );
    case ECertificationType.GITHUB:
      return (
        <Github className="h-4 w-4" />
      );
    case ECertificationType.ORACLE:
      return (
        <Database className="h-4 w-4" />
      );
    case ECertificationType.MONGODB:
      return (
        <Leaf className="h-4 w-4" />
      );
    case ECertificationType.ISQI:
      return (
        <Award className="h-4 w-4" />
      );
    case ECertificationType.BRIGHTEST:
      return (
        <Star className="h-4 w-4" />
      );
    case ECertificationType.CERTIFPROF:
      return (
        <GraduationCap className="h-4 w-4" />
      );
    case ECertificationType.SCRUMALLIANCE:
      return (
        <Users className="h-4 w-4" />
      );
    case ECertificationType.SCRUMINC:
      return (
        <GitBranch className="h-4 w-4" />
      );
    case ECertificationType.SCRUMORG:
      return (
        <Network className="h-4 w-4" />
      );
    case ECertificationType.ICAGILE:
      return (
        <Rocket className="h-4 w-4" />
      );
    case ECertificationType.PROKANBAN:
      return (
        <Columns3 className="h-4 w-4" />
      );
    case ECertificationType.CODERHOUSE:
      return (
        <Code className="h-4 w-4" />
      );
    case ECertificationType.OTHER:
      return (
        <Award className="h-4 w-4" />
      );
    default:
      return (
        <FileBadge className="h-4 w-4" />
      );
  }
};

// Utility function to determine certification status
export const getCertificationStatus = (expiracyDate?: Date) => {
  if (!expiracyDate) return "active";

  const now = moment();
  const expiry = moment(expiracyDate);
  const daysUntilExpiry = expiry.diff(now, "days");

  if (daysUntilExpiry < 0) return "expired";
  if (daysUntilExpiry <= 30) return "expiring-soon";
  return "active";
};

// Utility function to get status styling
export const getStatusStyling = (status: string) => {
  switch (status) {
    case "expired":
      return {
        badge: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
        border: "border-red-200 dark:border-red-700",
        icon: "text-red-500"
      };
    case "expiring-soon":
      return {
        badge:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        border: "border-yellow-200 dark:border-yellow-700",
        icon: "text-yellow-500"
      };
    default:
      return {
        badge:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        border: "border-green-200 dark:border-green-700",
        icon: "text-green-500"
      };
  }
};

// Utility function to get certification type styling
export const getCertificationTypeStyling = (type?: ETypeCertification) => {
  switch (type) {
    case ETypeCertification.COURSE:
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case ETypeCertification.ACREDITATION:
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
    case ETypeCertification.CREDENTIAL:
      return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
  }
};

interface ICertificationCardProps extends IUserCertification {
  disableEdit?: boolean;
}

// Skeleton loader component
export const CertificationCardSkeleton = () => {
  return (
    <Card variant="default" padding="sm" size="full">
      <div className="absolute left-0 right-0 top-0 h-1 bg-slate-200 text-left dark:bg-zinc-700" />
      <div className="animate-pulse">
        <div className="mb-3 flex items-start justify-between gap-3 sm:mb-4 sm:gap-4">
          <div className="flex flex-1 items-start gap-2 sm:gap-3">
            <div className="h-8 w-8 flex-shrink-0 rounded-lg bg-slate-200 dark:bg-zinc-700 sm:h-12 sm:w-12" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 rounded bg-slate-200 dark:bg-zinc-700 sm:h-5" />
              <div className="h-3 w-1/2 rounded bg-slate-200 dark:bg-zinc-700 sm:h-4" />
            </div>
          </div>
          <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-slate-200 dark:bg-zinc-700" />
        </div>

        <div className="mb-3 flex gap-2 sm:mb-4">
          <div className="h-6 w-20 rounded-full bg-slate-200 dark:bg-zinc-700" />
          <div className="h-6 w-24 rounded-full bg-slate-200 dark:bg-zinc-700" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded bg-slate-200 dark:bg-zinc-700" />
            <div className="h-3 w-40 rounded bg-slate-200 dark:bg-zinc-700" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded bg-slate-200 dark:bg-zinc-700" />
            <div className="h-3 w-36 rounded bg-slate-200 dark:bg-zinc-700" />
          </div>
        </div>

        <div className="mt-4 border-t border-slate-200/70 pt-3 dark:border-zinc-800">
          <div className="h-9 w-full rounded-lg bg-slate-200 dark:bg-zinc-700" />
        </div>
      </div>
    </Card>
  );
};

// Empty state component
export const CertificationCardsEmpty = () => {
  const { t } = useTranslation("common");

  return (
    <Card variant="default" padding="comfortable" size="full">
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 dark:bg-zinc-700">
          <Award className="h-8 w-8 text-2xl text-slate-400" />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">
            {t("Sin certificaciones")}
          </h3>
          <p className="max-w-sm text-sm text-slate-600 dark:text-zinc-400">
            {t(
              "Aquí aparecerán las certificaciones que has obtenido. Añade tu primera certificación para comenzar."
            )}
          </p>
        </div>

        <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
          {t("Añadir certificación")}
        </Button>
      </div>
    </Card>
  );
};

const CertificationCard = (props: ICertificationCardProps) => {
  const {
    type,
    name,
    expiracyDate,
    certificationUrl,
    url,
    id,
    disableEdit,
    emitDate,
    certificationType
  } = props;
  const { t } = useTranslation("common");
  const [editOpen, setEditOpen] = useState(false);
  const wwidth = useWindowWidth();
  const isMd = wwidth >= WIDTH_MD;

  const [deleteCertification] = useMutation<
    { deleteUserCertification: IDeleteUserCertificationRes },
    { deleteUserCertificationInput: IDeleteUserCertificationInput }
  >(gqlUser.mutations.DELETE_USER_CERTIFICATION, {
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_MY_SPACE)],
    awaitRefetchQueries: true
  });

  // Get certification status and styling
  const status = getCertificationStatus(expiracyDate);
  const statusStyling = getStatusStyling(status);

  const handleDeleteCertification = (id: string) => {
    toast.promise(
      deleteCertification({
        variables: {
          deleteUserCertificationInput: {
            id
          }
        }
      }),
      {
        loading: t("Eliminando certificación"),
        success: t("Certificación eliminada"),
        error: t("Error al eliminar certificación")
      }
    );
  };

  const handleUpdateCertification = () => {
    setEditOpen(true);
  };

  const handleDownload = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.setAttribute("download", "certification.pdf");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "expired":
        return t("Expirada");
      case "expiring-soon":
        return t("Expira pronto");
      default:
        return t("Activa");
    }
  };

  const getStatusIcon = (status: string): LucideIcon => {
    switch (status) {
      case "expired":
        return AlertTriangle;
      case "expiring-soon":
        return Clock;
      default:
        return CheckCircle;
    }
  };

  const StatusIcon = getStatusIcon(status);

  const actionItems: LiquidPillDropdownItem[] = [
    ...(url
      ? [
          {
            value: "download",
            label: t("Descargar"),
            icon: Download,
            variant: "slate" as const
          }
        ]
      : []),
    ...(certificationUrl
      ? [
          {
            value: "view",
            label: t("Ver certificación"),
            icon: ExternalLink,
            variant: "indigo" as const
          }
        ]
      : []),
    {
      value: "edit",
      label: t("Editar"),
      icon: Pencil,
      variant: "indigo" as const
    },
    {
      value: "delete",
      label: t("Eliminar"),
      icon: Trash2,
      variant: "rose" as const
    }
  ];

  const handleActionSelect = (value: string) => {
    switch (value) {
      case "download":
        if (url) handleDownload(url);
        break;
      case "view":
        if (certificationUrl) handleDownload(certificationUrl);
        break;
      case "edit":
        handleUpdateCertification();
        break;
      case "delete":
        handleDeleteCertification(id);
        break;
    }
  };

  return (
    <>
      {editOpen && (
        <CertificationForm open certification={props} onClose={() => setEditOpen(false)} />
      )}
      <Card variant="default" padding="sm" size="full" fullHeight>
      {/* Status indicator bar */}
      <div
        className={`absolute left-0 right-0 top-0 h-1 ${status === "expired" ? "bg-red-500" : status === "expiring-soon" ? "bg-yellow-500" : "bg-green-500"}`}
      />

      <div className="flex h-full flex-col text-left">
        {/* Header with icon, title and actions */}
        <div className="mb-3 flex items-start justify-between gap-3 sm:mb-4 sm:gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-2 sm:gap-3">
            {/* Certification icon */}
            <div className="mt-1 flex-shrink-0">
              {getIconByType(type as ECertificationType, isMd ? 48 : 32)}
            </div>

            {/* Title and provider */}
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 line-clamp-2 text-base font-semibold leading-tight text-slate-900 dark:text-white sm:text-lg">
                {name}
              </h3>
              <p className="text-xs font-medium text-slate-600 dark:text-zinc-400 sm:text-sm">
                {type}
              </p>
            </div>
          </div>

          {/* Actions menu */}
          {!disableEdit && (
            <div className="flex-shrink-0">
              <LiquidPillDropdown
                items={actionItems}
                onSelect={handleActionSelect}
                icon={<MoreVertical className="h-4 w-4" />}
                variant="slate"
                size="sm"
                collapsedShape="circle"
                reactive
                aria-label={t("Opciones")}
              />
            </div>
          )}
        </div>

        {/* Status and type badges */}
        <div className="mb-3 flex flex-wrap items-center gap-2 sm:mb-4">
          <GlassBadge
            variant="slate"
            active={status === "expired" || status === "expiring-soon"}
            size="sm"
            as="div"
          >
            <StatusIcon className="h-3 w-3" />
            <span>{getStatusText(status)}</span>
          </GlassBadge>

          <GlassBadge
            variant="indigo"
            active={true}
            size="sm"
            as="div"
          >
            {certificationType || ETypeCertification.CERTIFICATION}
          </GlassBadge>
        </div>

        {/* Dates information */}
        <div className="space-y-2 text-xs text-slate-600 dark:text-zinc-400 sm:text-sm">
          {emitDate && (
            <div className="flex items-start gap-2 sm:items-center">
              <CalendarPlus className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400 sm:mt-0" />
              <span className="break-words">
                {t("Emitida el")}:{" "}
                <span className="font-medium">
                  {moment(emitDate).format("DD/MM/YYYY")}
                </span>
              </span>
            </div>
          )}

          {expiracyDate && (
            <div className="flex items-start gap-2 sm:items-center">
              <CalendarX className="h-4 w-4" />
              <div className="break-words">
                <span>
                  {t("Válida hasta")}:{" "}
                  <span
                    className={`font-medium ${status === "expired" ? "text-red-600 dark:text-red-400" : status === "expiring-soon" ? "text-yellow-600 dark:text-yellow-400" : ""}`}
                  >
                    {moment(expiracyDate).format("DD/MM/YYYY")}
                  </span>
                </span>
                {status === "expiring-soon" && (
                  <span className="block text-xs text-yellow-600 dark:text-yellow-400 sm:ml-2 sm:inline">
                    ({moment(expiracyDate).diff(moment(), "days")} días
                    restantes)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Quick actions for downloadable content */}
        {(url || certificationUrl) && (
          <div className="mt-auto border-t border-slate-200 pt-3 dark:border-zinc-700">
            <div className="flex flex-col gap-2 sm:flex-row">
              {url && (
                <Button
                  variant="ghost"
                  size="sm"
                  width="full"
                  leftIcon={<CloudDownload className="h-4 w-4" />}
                  onClick={() => handleDownload(url)}
                >
                  {t("Descargar")}
                </Button>
              )}

              {certificationUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  width="full"
                  leftIcon={<ExternalLink className="h-4 w-4" />}
                  onClick={() => handleDownload(certificationUrl)}
                >
                  {t("Ver certificación")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
    </>
  );
};

export default CertificationCard;
